import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Popover,
  Drawer,
  IconButton,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Slider,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  Divider,
  useMediaQuery,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import CustomSearchBar from "./CustomSearchBar"

const CustomFilter = ({
  filtersConfig = [],
  values = {},
  onChange,
  onApply,
  onClear,
  onSearch,
  searchValue = "",
  searchPlaceholder = "Search...",
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);

  const isMobile = useMediaQuery("(max-width:768px)");
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((event) => {
    if (isMobile) setOpenDrawer(true);
    else setAnchorEl(event.currentTarget);
  }, [isMobile]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setOpenDrawer(false);
  }, []);

  // FIXED HANDLER
  const handleChange = useCallback((key, value, type) => {
    if (type === "checkbox") {
      onChange(key, value, "toggle");
    } else if (type === "range") {
      onChange(key, value, "single", "greater_than"); // ✅ operator separate
    } else if (type === "date_range") {
      onChange(key, value, "single", "date_range");
    } else {
      onChange(key, value, "single");
    }
  }, [onChange]);

  const renderField = useCallback((filter) => {
    const value = values[filter.key];

    switch (filter.type) {
      case "checkbox":
        return (
          <FormGroup>
            {filter.options.map((opt) => (
              <FormControlLabel
                key={opt.value}
                control={
                  <Checkbox
                    checked={(value || []).includes(opt.value)}
                    onChange={() =>
                      handleChange(filter.key, opt.value, "checkbox")
                    }
                  />
                }
                label={opt.label}
              />
            ))}
          </FormGroup>
        );

      case "radio":
        return (
          <RadioGroup
            value={(value && value[0]) || ""}
            onChange={(e) =>
              handleChange(filter.key, e.target.value, "radio")
            }
          >
            {filter.options.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio />}
                label={opt.label}
              />
            ))}
          </RadioGroup>
        );

      case "select":
        return (
          <Select
            fullWidth
            size="small"
            value={(value && value[0]) || ""}
            onChange={(e) =>
              handleChange(filter.key, e.target.value, "select")
            }
          >
            {filter.options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        );

      case "range":
        return (
          <Box px={1}>
            <Slider
              value={value?.value ?? filter.min ?? 0}
              min={filter.min || 0}
              max={filter.max || 100}
              step={filter.step || 1}
              valueLabelDisplay="auto"
              onChange={(e, val) =>
                handleChange(filter.key, val, "range")
              }
            />
          </Box>
        );

      case "text":
        return (
          <TextField
            fullWidth
            size="small"
            value={typeof value === "object" && value?.value != null ? value.value : (value ?? "")}
            placeholder={filter.placeholder || ""}
            onChange={(e) => handleChange(filter.key, e.target.value, "text")}
          />
        );

      case "date_range":
        return (
          <Box display="flex" gap={1}>
            <TextField
              type="date"
              size="small"
              label="From"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: value?.value?.end || undefined }}
              value={value?.value?.start || ""}
              onChange={(e) =>
                handleChange(filter.key, { ...(value?.value || {}), start: e.target.value }, "date_range")
              }
              fullWidth
            />
            <TextField
              type="date"
              size="small"
              label="To"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: value?.value?.start || undefined }}
              value={value?.value?.end || ""}
              onChange={(e) =>
                handleChange(filter.key, { ...(value?.value || {}), end: e.target.value }, "date_range")
              }
              fullWidth
            />
          </Box>
        );
      default:
        return null;
    }
  }, [values, handleChange]);

  const filterContent = useMemo(() => (
    <Box>
      <Grid container spacing={3}>
        {filtersConfig.map((filter) => (
          <Grid item xs={12} sm={6} md={4} key={filter.key}>
            <Typography sx={{ mb: 1, fontWeight: 600 }}>
              {filter.label}
            </Typography>
            {renderField(filter)}
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="outlined"
          onClick={() => {
            onClear?.();     // clear filters
            handleClose();   // close popover/drawer
          }}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onApply();
            handleClose();
          }}
        >
          Apply filters
        </Button>
      </Box>
    </Box>
  ), [filtersConfig, renderField, onClear, onApply, handleClose]);

  return (
    <>
      <Box display="flex" gap={2} alignItems="center">
        <CustomSearchBar
          placeholder={searchPlaceholder}
          size="small"
          fullWidth
          value={searchValue}
          onChange={(e) => onSearch?.(e.target.value)}
          onClear={() => onSearch?.("")}
        />

        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={handleOpen}
        >
          Filters
        </Button>
      </Box>

      {!isMobile && (
        <Popover 
           open={open} 
           anchorEl={anchorEl} 
           onClose={handleClose}
           anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
           transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box p={2} width={650}>{filterContent}</Box>
        </Popover>
      )}

      {isMobile && (
        <Drawer anchor="bottom" open={openDrawer} onClose={handleClose}>
          <Box p={2}>
            <Box display="flex" justifyContent="flex-end">
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            {filterContent}
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default React.memo(CustomFilter);